import type {
  AccessRequest,
  AdminStats,
  AdminApp,
  AdminUser,
  AppDetail,
  FleetHealth,
  AppSummary,
  AuditFilters,
  AuditPage,
  DashboardStats,
  Feedback,
  FeedbackProposal,
  FeedbackRequestRow,
  FeedbackStats,
  FeedbackSubmit,
  Grant,
  MaintenanceStatus,
  ProvisionRow,
  RecommendResponse,
  RequestFeedbackResult,
  UserRole,
  WhoAmI,
} from "./types";

function auditQs(f: AuditFilters): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  }
  return p.toString();
}

const BASE = import.meta.env.VITE_API_BASE ?? "";

export class HttpError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new HttpError(res.status, `${res.status} ${res.statusText}`);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  whoami: () => req<WhoAmI>("/api/whoami"),
  listApps: (type?: string) =>
    req<AppSummary[]>(`/api/apps${type ? `?app_type=${encodeURIComponent(type)}` : ""}`),
  getApp: (key: string) => req<AppDetail>(`/api/apps/${encodeURIComponent(key)}`),
  // Preview apps (super-admins only; returns [] for everyone else).
  listPreview: () => req<AppSummary[]>("/api/apps/preview"),
  adminSetVisibility: (key: string, is_visible: boolean) =>
    req<void>(`/api/admin/apps/${encodeURIComponent(key)}/visibility`, {
      method: "PUT",
      body: JSON.stringify({ is_visible }),
    }),
  listFavourites: () => req<AppSummary[]>("/api/favourites"),
  addFavourite: (key: string) =>
    req<void>(`/api/apps/${encodeURIComponent(key)}/favourite`, { method: "PUT" }),
  removeFavourite: (key: string) =>
    req<void>(`/api/apps/${encodeURIComponent(key)}/favourite`, { method: "DELETE" }),
  reorderFavourites: (keys: string[]) =>
    req<void>("/api/favourites/order", { method: "PUT", body: JSON.stringify({ keys }) }),
  requestAccess: (key: string, message: string) =>
    req<{ status: string }>(`/api/apps/${encodeURIComponent(key)}/request-access`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),
  recommend: (query: string) =>
    req<RecommendResponse>("/api/assistant/recommend", {
      method: "POST",
      body: JSON.stringify({ query }),
    }),
  askApp: (key: string, question: string, history: { role: string; content: string }[]) =>
    req<{ answer: string }>(`/api/apps/${encodeURIComponent(key)}/ask`, {
      method: "POST",
      body: JSON.stringify({ question, history }),
    }),
  askHub: (question: string, history: { role: string; content: string }[]) =>
    req<{ answer: string }>("/api/assistant/ask", {
      method: "POST",
      body: JSON.stringify({ question, history }),
    }),
  adminListRequests: (status = "pending") =>
    req<AccessRequest[]>(`/api/admin/access-requests?status_filter=${encodeURIComponent(status)}`),
  adminApprove: (id: string, note?: string) =>
    req<AccessRequest>(`/api/admin/access-requests/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  adminDeny: (id: string, note?: string) =>
    req<AccessRequest>(`/api/admin/access-requests/${encodeURIComponent(id)}/deny`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  adminStats: () => req<AdminStats>("/api/admin/stats"),
  adminFleetHealth: () => req<FleetHealth>("/api/admin/fleet-health"),
  adminAudit: (f: AuditFilters = {}) => req<AuditPage>(`/api/admin/audit?${auditQs(f)}`),
  adminAuditStats: (f: AuditFilters = {}) => req<DashboardStats>(`/api/admin/audit/stats?${auditQs(f)}`),
  adminApps: () => req<AdminApp[]>("/api/admin/apps"),
  adminSetGlobalAccess: (key: string, global: boolean) =>
    req<AdminApp>(`/api/admin/apps/${encodeURIComponent(key)}/global-access`, {
      method: "PUT",
      body: JSON.stringify({ global_access: global }),
    }),
  adminSetRestricted: (key: string, restricted: boolean) =>
    req<AdminApp>(`/api/admin/apps/${encodeURIComponent(key)}/restricted`, {
      method: "PUT",
      body: JSON.stringify({ restricted }),
    }),
  adminSetAccessPolicy: (key: string, self_request: boolean, access_note?: string) =>
    req<AdminApp>(`/api/admin/apps/${encodeURIComponent(key)}/access-policy`, {
      method: "PUT",
      body: JSON.stringify({ self_request, access_note: access_note ?? null }),
    }),
  adminProvisions: () => req<ProvisionRow[]>("/api/admin/provisions"),
  adminProvisionsRetry: () =>
    req<{ promoted: number }>("/api/admin/provisions/retry", { method: "POST" }),
  adminGrants: () => req<Grant[]>("/api/admin/grants"),
  adminUsers: () => req<AdminUser[]>("/api/admin/users"),
  adminCreateUser: (email: string, name?: string) =>
    req<AdminUser>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify({ email, name: name ?? null }),
    }),
  adminSetUserRole: (email: string, role: UserRole) =>
    req<AdminUser>(`/api/admin/users/${encodeURIComponent(email)}/role`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    }),
  adminDeleteUser: (email: string) =>
    req<void>(`/api/admin/users/${encodeURIComponent(email)}`, { method: "DELETE" }),
  adminGrant: (key: string, email: string) =>
    req<void>(`/api/admin/apps/${encodeURIComponent(key)}/grant`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  adminRevoke: (key: string, email: string) =>
    req<void>(`/api/admin/apps/${encodeURIComponent(key)}/revoke`, {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  // feedback loop
  submitFeedback: (body: FeedbackSubmit) =>
    req<Feedback>("/api/feedback", { method: "POST", body: JSON.stringify(body) }),
  adminRequestFeedback: (emails: string[], campaign?: string) =>
    req<RequestFeedbackResult>("/api/admin/feedback/request", {
      method: "POST",
      body: JSON.stringify({ emails, campaign: campaign ?? null }),
    }),
  adminFeedbackStats: () => req<FeedbackStats>("/api/admin/feedback/stats"),
  adminSuggestFeedback: (days = 7, minEvents = 3) =>
    req<{ days: number; proposals: FeedbackProposal[] }>(
      `/api/admin/feedback/suggest?days=${days}&min_events=${minEvents}`,
      { method: "POST" },
    ),
  adminSendSuggested: (items: { email: string; app_key: string; prompt: string }[]) =>
    req<RequestFeedbackResult>("/api/admin/feedback/send-suggested", {
      method: "POST",
      body: JSON.stringify({ items }),
    }),
  adminFeedbackList: (status?: string) =>
    req<{ total: number; rows: Feedback[] }>(
      `/api/admin/feedback${status ? `?status_filter=${encodeURIComponent(status)}` : ""}`,
    ),
  adminFeedbackRequests: (status?: string) =>
    req<{ total: number; rows: FeedbackRequestRow[] }>(
      `/api/admin/feedback/requests${status ? `?status_filter=${encodeURIComponent(status)}` : ""}`,
    ),
  adminActFeedback: (id: string, note?: string) =>
    req<Feedback>(`/api/admin/feedback/${encodeURIComponent(id)}/act`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  adminShelveFeedback: (id: string, note?: string) =>
    req<Feedback>(`/api/admin/feedback/${encodeURIComponent(id)}/shelve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  // maintenance
  getMaintenance: () => req<MaintenanceStatus>("/api/maintenance"),
  adminSetHubMaintenance: (on: boolean, message?: string) =>
    req<{ on: boolean; message: string | null }>("/api/admin/maintenance", {
      method: "PUT",
      body: JSON.stringify({ on, message: message ?? null }),
    }),
  adminSetAppMaintenance: (key: string, on: boolean, message?: string) =>
    req<AdminApp>(`/api/admin/apps/${encodeURIComponent(key)}/maintenance`, {
      method: "PUT",
      body: JSON.stringify({ on, message: message ?? null }),
    }),
};

/** Full-navigation hrefs (these hit the backend, which 302s onward). */
export const launchHref = (key: string) => `${BASE}/api/apps/${encodeURIComponent(key)}/launch`;
export const loginHref = () => `${BASE}/auth/login`;
export const logoutHref = () => `${BASE}/logout`;
